/**
 * Detalle de un grupo del reporte de compras (proveedor + estado): los
 * documentos que suman la fila, paginados por el servidor (#1488).
 *
 * ⚠️ Es `GET /purchases/invoices/` con `supplier_id`, `status` y las mismas
 * fechas del reporte. El listado excluye honorarios y filtra por `issue_date`
 * igual que `PurchasesReportView`, que es lo que hace cuadrar la suma de estos
 * documentos con la fila. Si el reporte agrega una exclusión, va también allá.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { fmtDate } from '@/lib/utils'
import { useTenantLocale } from '@/lib/locale'
import { DataTable } from '@/components/ds/DataTable'
import { Pagination } from '@/components/ds/Pagination'
import { EmptyState } from '@/components/EmptyState'
import {
  useSupplierInvoices, type SupplierInvoiceListItem,
} from '@/modules/purchases/hooks/usePurchases'

const PAGE_SIZE = 10

export interface PurchasesGroupDetailProps {
  supplierId: string
  status: string
  dateFrom?: string
  dateTo?: string
  /** Nombre del impuesto del tenant (IVA, IGV…), el mismo de la tabla del reporte. */
  taxName: string
  fmtCur: (v: string | number | undefined) => string
  /** Con la Vista 360 activa el número es un enlace a ella; sin el flag, abre el
   *  modal de detalle clásico (el 360 devuelve al listado si el flag está apagado). */
  use360: boolean
  onOpenInvoice: (id: string) => void
}

export function PurchasesGroupDetail({
  supplierId, status, dateFrom, dateTo, taxName, fmtCur, use360, onOpenInvoice,
}: PurchasesGroupDetailProps) {
  // ⚠️ Sin orden por clic a propósito: la lista la pagina el servidor, y ordenar
  // en el navegador ordenaría solo la página visible sin avisar.
  const locale = useTenantLocale()
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useSupplierInvoices({
    supplier_id: supplierId,
    status,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    ordering: '-issue_date',
    page,
    page_size: PAGE_SIZE,
  })

  if (isError) {
    return <p className="px-4 py-3 text-sm text-danger-solid">No se pudieron cargar los documentos del grupo.</p>
  }

  return (
    <div className="px-4 py-3 bg-soft">
      <DataTable<SupplierInvoiceListItem>
        columns={[
          { key: 'document_type_sii', header: 'Tipo',
            render: inv => <span className="font-mono">{inv.document_type_sii ?? '—'}</span> },
          { key: 'invoice_number', header: 'N°',
            render: inv => (use360 ? (
              <Link
                to={`/purchases/invoices/${inv.id}/360`}
                onClick={e => e.stopPropagation()}
                className="font-mono text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring rounded"
              >
                {inv.invoice_number || '—'}
              </Link>
            ) : (
              <button
                type="button"
                onClick={e => { e.stopPropagation(); onOpenInvoice(inv.id) }}
                className="font-mono text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring rounded"
                aria-label={`Ver el documento ${inv.invoice_number || ''}`.trim()}
              >
                {inv.invoice_number || '—'}
              </button>
            )) },
          { key: 'issue_date', header: 'Fecha',
            render: inv => fmtDate(inv.issue_date, locale) },
          { key: 'subtotal', header: 'Neto', numeric: true, render: inv => fmtCur(inv.subtotal) },
          { key: 'tax_amount', header: taxName, numeric: true, render: inv => fmtCur(inv.tax_amount) },
          { key: 'total', header: 'Total', numeric: true,
            render: inv => <span className="font-semibold">{fmtCur(inv.total)}</span> },
        ]}
        rows={data?.invoices ?? []}
        rowKey={inv => inv.id}
        loading={isLoading}
        onRowClick={inv => onOpenInvoice(inv.id)}
        surface={false}
        density="dense"
        empty={<EmptyState title="Sin documentos en el grupo" size="sm" />}
        caption="Documentos de compra del proveedor en el estado y período del grupo"
      />
      {data && data.count > PAGE_SIZE && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={data.count}
          onPageChange={setPage}
          locale={locale}
          className="mt-3"
        />
      )}
    </div>
  )
}
